// Approval of purchase orders on the device (docs/plans/po-approvals.md). The server
// decides (decide_document, online) and refuses what these say no to; the device uses the
// same rules to offer "Submit for approval" instead of "Send" and to show whose turn it is.
import type { RowOf } from '@ims/data'

export type MemberRole = 'viewer' | 'waiter' | 'staff' | 'manager' | 'admin' | 'owner'
const ORDER: MemberRole[] = ['viewer', 'waiter', 'staff', 'manager', 'admin', 'owner']
export const roleAtLeast = (role: MemberRole, min: MemberRole) =>
  ORDER.indexOf(role) >= ORDER.indexOf(min)

export type ApprovalRule = Pick<
  RowOf<'approval_rules'>,
  'id' | 'doc_type' | 'level' | 'min_amount_paisa' | 'approver_role' | 'deleted_at'
>
export type Decision = Pick<
  RowOf<'document_approvals'>,
  'level' | 'decision' | 'decided_by' | 'note' | 'created_at'
>

export interface Step {
  level: number
  role: 'manager' | 'admin' | 'owner'
}

/** The steps an order of this total needs, in order (`_approval_steps` on the server). */
export function approvalSteps(
  rules: readonly ApprovalRule[],
  totalPaisa: number,
  docType: ApprovalRule['doc_type'] = 'purchase_order',
): Step[] {
  return rules
    .filter((r) => !r.deleted_at && r.doc_type === docType && r.min_amount_paisa <= totalPaisa)
    .sort((a, b) => a.level - b.level)
    .map((r) => ({ level: r.level, role: r.approver_role }))
}

/** Whether an order of this total is submitted rather than sent. */
export function needsApproval(
  rules: readonly ApprovalRule[],
  totalPaisa: number,
  featureOn: boolean,
  docType: ApprovalRule['doc_type'] = 'purchase_order',
): boolean {
  return featureOn && approvalSteps(rules, totalPaisa, docType).length > 0
}

/** The first step that applies and has not said yes; null when every step has. */
export function nextStep(steps: readonly Step[], decisions: readonly Decision[]): Step | null {
  const yes = new Set(decisions.filter((d) => d.decision === 'approved').map((d) => d.level))
  return steps.find((s) => !yes.has(s.level)) ?? null
}

export interface Approver {
  userId: string
  role: MemberRole
  /** null: no limit of their own. */
  limitPaisa: number | null
}

/**
 * Whether this person may decide the order now, and if not, why (in words for the
 * screen). Mirrors decide_document: their step, their role, their own limit, not their
 * own order, one step each.
 */
export function mayDecide(
  order: { created_by?: string | null; total_paisa: number; status: string },
  steps: readonly Step[],
  decisions: readonly Decision[],
  me: Approver,
  what: 'order' | 'request' = 'order',
): { ok: true; step: Step } | { ok: false; why: string } {
  if (order.status !== 'submitted') return { ok: false, why: 'It is not waiting for approval.' }
  if (order.created_by === me.userId)
    return { ok: false, why: `You made this ${what}; someone else approves it.` }
  if (decisions.some((d) => d.decided_by === me.userId))
    return { ok: false, why: `You approved a step of this ${what} already.` }
  const step = nextStep(steps, decisions) ?? {
    level: Math.max(0, ...decisions.map((d) => d.level)) + 1,
    role: 'manager' as const,
  }
  if (!roleAtLeast(me.role, step.role))
    return { ok: false, why: `Step ${step.level} needs ${ROLE_WORDS[step.role]}.` }
  if (me.limitPaisa !== null && order.total_paisa > me.limitPaisa)
    return { ok: false, why: 'It is above your approval limit.' }
  return { ok: true, step }
}

export const ROLE_WORDS: Record<Step['role'], string> = {
  manager: 'a manager or above',
  admin: 'an admin or the owner',
  owner: 'the owner',
}

/** "Manager or above", for the steps table and an order's steps. */
export const ROLE_LABEL: Record<Step['role'], string> = {
  manager: 'Manager or above',
  admin: 'Admin or owner',
  owner: 'Owner',
}
